const TARGET_SAMPLE_RATE = 16000;
const TRANSFORMERS_URL =
  "https://cdn.jsdelivr.net/npm/@huggingface/transformers@3.5.1";

let transcriberPromise = null;

const rms = (samples) => {
  if (!samples?.length) return 0;
  let sum = 0;
  for (let i = 0; i < samples.length; i += 1) {
    sum += samples[i] * samples[i];
  }
  return Math.sqrt(sum / samples.length);
};

const toMono = (audioBuffer) => {
  const length = audioBuffer.length;
  const channels = audioBuffer.numberOfChannels;
  if (channels === 1) {
    return audioBuffer.getChannelData(0).slice();
  }
  const mixed = new Float32Array(length);
  for (let i = 0; i < length; i += 1) {
    let sum = 0;
    for (let channel = 0; channel < channels; channel += 1) {
      sum += audioBuffer.getChannelData(channel)[i];
    }
    mixed[i] = sum / channels;
  }
  return mixed;
};

const resampleLinear = (input, fromRate, toRate) => {
  if (fromRate === toRate) return input;
  const ratio = fromRate / toRate;
  const outLength = Math.max(1, Math.round(input.length / ratio));
  const output = new Float32Array(outLength);
  for (let i = 0; i < outLength; i += 1) {
    const position = i * ratio;
    const index = Math.floor(position);
    const next = Math.min(index + 1, input.length - 1);
    const fraction = position - index;
    output[i] = input[index] * (1 - fraction) + input[next] * fraction;
  }
  return output;
};

export const decodeBlobToMono16k = async (blob) => {
  const AudioCtx = window.AudioContext || window.webkitAudioContext;
  if (!AudioCtx) {
    throw new Error("Web Audio is not available in this browser.");
  }
  const context = new AudioCtx();
  try {
    const audioBuffer = await context.decodeAudioData(await blob.arrayBuffer());
    const mono = toMono(audioBuffer);
    return resampleLinear(mono, audioBuffer.sampleRate, TARGET_SAMPLE_RATE);
  } finally {
    await context.close?.();
  }
};

const loadTranscriber = async () => {
  if (transcriberPromise) return transcriberPromise;

  transcriberPromise = (async () => {
    const importEsm = (url) => new Function("u", "return import(u)")(url);
    const transformers = await importEsm(
      `${TRANSFORMERS_URL}/+esm`,
    );
    const { pipeline, env } = transformers;
    env.allowLocalModels = false;
    env.useBrowserCache = true;
    if (env.backends?.onnx?.wasm) {
      env.backends.onnx.wasm.wasmPaths = `${TRANSFORMERS_URL}/dist/`;
    }
    return pipeline("automatic-speech-recognition", "Xenova/whisper-tiny.en", {
      quantized: true,
    });
  })().catch((error) => {
    transcriberPromise = null;
    throw error;
  });

  return transcriberPromise;
};

export const transcribeRecordedAudio = async (blob) => {
  if (!blob || blob.size < 800) {
    return "";
  }

  const samples = await decodeBlobToMono16k(blob);
  if (rms(samples) < 0.008) {
    return "";
  }

  const transcriber = await loadTranscriber();
  // whisper-tiny.en is English-only; passing language/task throws.
  const result = await transcriber(samples);

  if (typeof result === "string") return result.trim();
  return String(result?.text ?? "").trim();
};
